// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RequestState } from "@utils/types";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { JdDocStates } from "./model";

export const getJdDoc = createAsyncThunk(
  "jobDescriptionDocumentContent/getJdDoc",
  async ({ id, taskInfo }: { id: number; taskInfo: any }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(
          `${AppConfig.serviceUrls.vacancies}/${id}/job-requisition/description`,
        )
        .then((response) => {
          resolve({
            res: response.data,
            taskInfo,
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

const initialState: JdDocStates = {
  jdHtmlObject: null,
  jdDocResponse: null,
  state: RequestState.IDLE,
};

const jobDescriptionDocumentContentSlice = createSlice({
  name: "jobDescriptionDocumentContent",
  initialState,
  reducers: {
    resetjdDoc: (state) => {
      state.jdHtmlObject = null;
      state.jdDocResponse = null;
      state.state = RequestState.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJdDoc.pending, (state) => {
      state.state = RequestState.LOADING;
    });

    builder.addCase(getJdDoc.fulfilled, (state, action) => {
      const { res, taskInfo } = action.payload;

      state.jdDocResponse = res;

      try {
        const contentObject = JSON.parse(res.content);
        const original = contentObject.originalContent ?? contentObject;

        const jdHtmlObjectData: any = {};
        jdHtmlObjectData.mainContent = original.mainContent ?? null;
        jdHtmlObjectData.taskInformation = taskInfo ?? null;
        jdHtmlObjectData.additionalContent = original.additionalContent ?? null;

        state.jdHtmlObject = JSON.stringify(jdHtmlObjectData);
        state.state = RequestState.SUCCEEDED;
      } catch {
        state.jdHtmlObject = null;
        state.state = RequestState.FAILED;
      }
    });

    builder.addCase(getJdDoc.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
  },
});

export const { resetjdDoc } = jobDescriptionDocumentContentSlice.actions;
export default jobDescriptionDocumentContentSlice.reducer;
